import type { KundeSummary, TodoDto, VorgangDto } from "../services/vorgangService";
import { labelForUserKey, type AppUser } from "../services/userService";

export type ScopeKey = "alle" | "team" | "persoenlich";

/**
 * Bereichsauswahl: WESSEN Arbeit die Liste zeigt.
 *
 * Der dritte Punkt hieß „Persönlich" — dasselbe Wort trägt aber auch das Badge
 * eines Vorgangs OHNE Kontakt (`persoenlich = true`), und in derselben Ansicht
 * meinten die beiden Verschiedenes: einmal „ich trage die Arbeit", einmal „der
 * Vorgang gehört nur mir". Er heißt deshalb „Eigene" (PO 06.09.2026: „Team und
 * Eigen kann man gut unterscheiden … dann ist das andere persönliche").
 *
 * Der SCHLÜSSEL bleibt `persoenlich`: er geht als `?scope=` an den Feed und
 * steckt in gespeicherten Ansichten — ein neues Wort dort wäre ein Bruch ohne
 * Gegenwert. Umbenannt ist, was der Anwender liest.
 */
export const SCOPE_OPTIONS: { key: ScopeKey; label: string }[] = [
    { key: "alle", label: "Alle" },
    { key: "team", label: "Team" },
    { key: "persoenlich", label: "Eigene" },
];

/**
 * Die Bereiche, die jemand wählen kann: „Team" nur mit Teamzugehörigkeit.
 * Gemeinsam für die Vorgänge-Seite und die Vorgangs-Picker (T-56).
 */
export function bereichOptionen(hatTeam: boolean): { key: ScopeKey; label: string }[] {
    return hatTeam ? SCOPE_OPTIONS : SCOPE_OPTIONS.filter((o) => o.key !== "team");
}

/**
 * Art des Vorgangs: WORAN er hängt — Kontakt-Vorgang oder persönlicher.
 * Zweite, unabhängige Achse neben dem Bereich, ausschließlich im Dashboard
 * (PO 07.09.2026: „die Auswahl Kontakt-Vorgänge und Persönliche im Dashboard
 * … und dann bitte nur die beiden").
 *
 * Bewusst OHNE „Alle": die Karte zeigt eine Sorte, und der Wechsel ist ein
 * Klick. Die volle Liste steht auf der Vorgänge-Seite, die diese Auswahl
 * deshalb gar nicht erst führt.
 */
export type VorgangArtKey = "kontakt" | "persoenlich";

export const ART_OPTIONS: { key: VorgangArtKey; label: string }[] = [
    { key: "kontakt", label: "Kontakt-Vorgänge" },
    { key: "persoenlich", label: "Persönliche" },
];

/** Womit die Karte startet — die Arbeit am Kunden ist der Regelfall. */
export const DEFAULT_ART: VorgangArtKey = "kontakt";

/** Passt der Vorgang zur gewählten Art? `persoenlich` = ohne Kontaktbezug. */
export function vorgangMatchesArt(
    v: { persoenlich?: boolean | null } | null | undefined,
    art: VorgangArtKey,
): boolean {
    const privat = v?.persoenlich === true;
    return art === "persoenlich" ? privat : !privat;
}

/**
 * Gehört dieser persönliche Vorgang MIR? Eigentümer ist `ownerUserId`, beim
 * Alt-Bestand ohne Eigentümer der Ersteller.
 */
export function istEigenerPersoenlicherVorgang(
    v: { persoenlich?: boolean | null; ownerUserId?: string | null; createdByUserId?: string | null } | null | undefined,
    userId: string | null | undefined,
): boolean {
    if (!v || v.persoenlich !== true || !userId) return false;
    return (v.ownerUserId ?? v.createdByUserId ?? null) === userId;
}

/**
 * Hat die Dashboard-Karte bei dieser Art überhaupt einen Bereich-Umschalter?
 *
 * PO 28.09.2026: „im Dashboard nicht mehr alle/team/eigene wenn es um
 * persönliche Vorgänge geht". Ein persönlicher Vorgang hat genau einen
 * Menschen — Team oder Alle gibt es dort nicht. Die Admin-Sicht sieht zwar
 * fremde persönliche Vorgänge, die gehören aber nicht auf SEIN Dashboard.
 */
export const bereichWaehlbar = (art: VorgangArtKey): boolean => art !== "persoenlich";

/** Filter der Dashboard-Vorgangskarte: bei „Persönliche" immer nur die eigenen. */
export function dashboardVorgangSichtbar(
    v: VorgangDto,
    art: VorgangArtKey,
    scope: ScopeKey,
    ctx: ScopeCtx,
): boolean {
    if (!vorgangMatchesArt(v, art)) return false;
    if (!bereichWaehlbar(art)) return istEigenerPersoenlicherVorgang(v, ctx.userId);
    return vorgangMatchesScope(v, scope, ctx);
}

/**
 * Rollen-abhängiger Default-Scope der Vorgänge-/Todo-Liste (A18):
 *  - admin           → „Alle" (Gesamtsicht)
 *  - alle anderen    → „Team" (ohne Team-Zugehörigkeit → „Persönlich")
 * `role` = better-auth-Rolle (roles[0]).
 */
export function defaultVorgangScope(role: string | null | undefined, hasTeam: boolean): ScopeKey {
    if (role === "admin") return "alle";
    return hasTeam ? "team" : "persoenlich";
}

/** Header label for the context-dependent person column. */
export const personColumnHeader = (scope: ScopeKey): string =>
    scope === "alle" ? "Verantwortlich (Team-BO)" : "Bearbeiter";

export interface ScopeCtx {
    userId: string | null;
    /** User-ids of everyone in my teams (incl. me). */
    teammates: string[];
    /** Ids of the teams I belong to. */
    teamIds: string[];
}

export function kundeName(k?: KundeSummary | null): string {
    if (!k) return "—";
    const n = [k.lastname, k.firstName].filter(Boolean).join(", ");
    return n || `Kontakt ${k.id}`;
}

/** Team-BO des Kunden (Verantwortlichkeit) — for the "Alle" view. */
export function responsibilityLabel(k?: KundeSummary | null): string {
    if (!k) return "—";
    if (k.boType === "TEAM") return k.boTeam?.name || "Team";
    if (k.boType === "USER") return k.boUser?.displayName || "—";
    return "—";
}

/** Normalises a bearbeiter JSON array (ids or {userId|id|kuerzel} objects) to ids/keys. */
export function bearbeiterKeys(bearbeiter: unknown[] | undefined): string[] {
    return (bearbeiter ?? [])
        .map((x) =>
            typeof x === "string"
                ? x
                : ((x as any)?.userId ?? (x as any)?.id ?? (x as any)?.kuerzel ?? null),
        )
        .filter((x): x is string => !!x);
}

/**
 * Der Teil des {@link ScopeCtx}, den die Schreibrechte brauchen: meine
 * Team-Zugehörigkeiten. Optional, weil nicht jede Aufrufstelle einen Kontext
 * führt — ohne ihn bleibt das Verhalten exakt wie vorher (nur die namentlich
 * Verantwortlichen zählen).
 */
export type TeamKontext = Pick<ScopeCtx, "teamIds">;

/**
 * `teams` aus dem AuthContext (`GET /rbac/me/permissions`) in den Team-Kontext
 * der Schreibrechte übersetzen — damit jede Aufrufstelle dieselbe, kurze Zeile
 * schreibt statt die Id-Liste selbst zu bauen.
 */
export function teamKontext(teams: ReadonlyArray<{ id: string }> | null | undefined): TeamKontext {
    return { teamIds: (teams ?? []).map((t) => t.id) };
}

/** Das BO-Team des Kunden — nur wenn die Verantwortlichkeit wirklich beim Team liegt. */
function boTeamIdVon(k: KundeSummary | null | undefined): string | null {
    if (!k || k.boType !== "TEAM") return null;
    return k.boTeam?.id ?? null;
}

/**
 * Kontaktverantwortlichkeit = FO, namentlicher BO-User **oder mein BO-Team**.
 *
 * Ein FO-Team gibt es nicht: `Contact.foUserId` ist immer genau eine Person.
 * Nur das Backoffice kann ein Team sein (`boType = TEAM`) — und dann trägt das
 * GANZE Team die Verantwortung (PO 16.09.2026: „die kontaktverantwortlichen fo
 * und bo (ganzes team) können den Vorgang und alle todos bearbeiten"). Bis
 * dahin sah ein BO-Team-Mitglied die Seite schreibgeschützt, obwohl das Backend
 * es beim Update längst durchließ.
 */
export function isKontaktverantwortlicher(
    k: KundeSummary | null | undefined,
    userId: string | null | undefined,
    ctx?: TeamKontext | null,
): boolean {
    if (!k || !userId) return false;
    if (k.foUser?.userId === userId || k.boUser?.userId === userId) return true;
    const boTeamId = boTeamIdVon(k);
    return !!boTeamId && (ctx?.teamIds ?? []).includes(boTeamId);
}

export function canEditVorgang(
    v: VorgangDto | null | undefined,
    userId: string | null | undefined,
    isAdmin: boolean,
    isGfgl: boolean,
    ctx?: TeamKontext | null,
): boolean {
    if (isAdmin) return true;
    // E07: Ein PERSÖNLICHER Vorgang gehört NUR seinem Eigentümer — auch `gfgl`
    // darf ihn nicht bearbeiten. Spiegelt `RbacService.canUserWrite`
    // („PRIVATE bleibt auch vor gfgl privat"). Der Check steht bewusst VOR dem
    // gfgl-Kurzschluss.
    if (v?.persoenlich) {
        return !!userId && (v.ownerUserId === userId || v.createdByUserId === userId);
    }
    if (isGfgl) return true;
    if (!v || !userId) return false;
    return (
        v.createdByUserId === userId ||
        (v.hauptverantwortlich ?? []).includes(userId) ||
        bearbeiterKeys(v.bearbeiter).includes(userId) ||
        isKontaktverantwortlicher(v.kunde, userId, ctx)
    );
}

export function canEditTodo(
    t: TodoDto | null | undefined,
    userId: string | null | undefined,
    isAdmin: boolean,
    isGfgl: boolean,
    ctx?: TeamKontext | null,
): boolean {
    if (isAdmin || isGfgl) return true;
    if (!t || !userId) return false;
    return (
        t.createdByUserId === userId ||
        (t.hauptverantwortlich ?? []).includes(userId) ||
        bearbeiterKeys(t.bearbeiter).includes(userId) ||
        isKontaktverantwortlicher(t.kunde, userId, ctx)
    );
}

/** Normalises a zuInformierende array (ids/keys) — analog zu bearbeiterKeys. */
export function zuInformierendeKeys(t: TodoDto | undefined): string[] {
    return bearbeiterKeys(t?.zuInformierende as unknown[] | undefined);
}

/* ────────── Info-Cluster zweiseitig (D6) ──────────
 * Abgeleitet NUR aus `zuInformierende` + Rolle — KEIN `art='info'`.
 *  - „Info erwartet" (Empfänger): ich stehe in zuInformierende UND bin nicht
 *    Bearbeiter/HV → passiver Beobachter, ich warte auf eine Info.
 *  - „Info geben" (Sender): ich bin Bearbeiter/HV/Ersteller UND zuInformierende
 *    ist nicht-leer → ich soll die hinterlegten Personen informieren. */

export const INFO_LABELS = { erwartet: "Info erwartet", geben: "Info geben" } as const;

/** Bin ich Bearbeiter oder Hauptverantwortlicher dieses To-Dos? */
function isBearbeiterOderHV(t: TodoDto, userId: string): boolean {
    const keys = [...bearbeiterKeys(t.bearbeiter), ...(t.hauptverantwortlich ?? [])];
    return keys.includes(userId);
}

/** „Info erwartet": ich stehe in zuInformierende UND bin nicht Bearbeiter/HV. */
export function isInfoEmpfaenger(t: TodoDto, ctx: ScopeCtx): boolean {
    if (!ctx.userId) return false;
    return zuInformierendeKeys(t).includes(ctx.userId) && !isBearbeiterOderHV(t, ctx.userId);
}

/** „Info geben": ich bin Bearbeiter/HV/Ersteller UND zuInformierende ist nicht-leer. */
export function isInfoSender(t: TodoDto, ctx: ScopeCtx): boolean {
    if (!ctx.userId) return false;
    if (zuInformierendeKeys(t).length === 0) return false;
    return isBearbeiterOderHV(t, ctx.userId) || t.ersteller === ctx.userId;
}

/** Info-Item = Empfänger ODER Sender (für den Info-Pill). */
export function isInfoItem(t: TodoDto, ctx: ScopeCtx): boolean {
    return isInfoEmpfaenger(t, ctx) || isInfoSender(t, ctx);
}

// Alias-Namen analog Plan-Terminologie (Info erwartet / Info geben).
export const isInfoErwartet = isInfoEmpfaenger;
export const isInfoGeben = isInfoSender;

/** Comma-separated display names for a bearbeiter list, resolved against the user directory. */
export function bearbeiterLabel(
    bearbeiter: unknown[] | undefined,
    users: AppUser[],
    loading = false,
): string {
    const keys = bearbeiterKeys(bearbeiter);
    if (keys.length === 0) return "—";
    return keys.map((k) => labelForUserKey(k, users, { loading })).join(", ");
}

/**
 * „Team" in den Vorgängen = der KUNDE gehört zu einem meiner BO-Teams
 * (`boType = TEAM`), sonst nichts (PO 01.11.2026, T-433).
 *
 * Bearbeiter (T-395) und FO/namentlicher BO (T-433: „wenn MaF BO ist, bitte mir
 * nicht Vorgang anzeigen") zählen nicht: Über firmenweite Teams wäre sonst fast
 * jeder Kunde „mein Team". Ohne Kunden gibt es kein „Team". Spiegelt
 * `VorgangService.kundeVomTeamVerantwortetSql` im Backend-Feed.
 */
export function kundeVomTeamVerantwortet(k: KundeSummary | null | undefined, ctx: ScopeCtx): boolean {
    if (!k) return false;
    const boTeamId = k.boType === "TEAM" ? (k.boTeamId ?? k.boTeam?.id ?? null) : null;
    return !!boTeamId && ctx.teamIds.includes(boTeamId);
}

export function vorgangMatchesScope(v: VorgangDto, scope: ScopeKey, ctx: ScopeCtx): boolean {
    if (scope === "alle") return true;
    const keys = bearbeiterKeys(v.bearbeiter);
    const todoKeys = (v.todos ?? []).flatMap((t) => bearbeiterKeys(t.bearbeiter));
    if (scope === "persoenlich") {
        if (!ctx.userId) return false;
        // „Persönlich" = wer die Arbeit trägt: Bearbeiter am Vorgang oder an
        // einem seiner To-dos, ODER Hauptverantwortlicher des Vorgangs (D2,
        // Review 2026-07-06).
        //
        // Der ERSTELLER zählt seit PO 04.09.2026 NICHT mehr mit („wenn ich nur
        // Ersteller bin und nicht Beteiligter, dann nicht unter Persönlich
        // anzeigen"). Wer ein To-do in einem fremden Vorgang anlegt, hat den
        // Vorgang damit nicht übernommen — über „Team"/„Alle" bleibt er
        // erreichbar. Gleichgezogen mit dem Backend-Feed
        // (vorgang.service.ts, scope === 'persoenlich').
        const hvKeys = v.hauptverantwortlich ?? [];
        return (
            keys.includes(ctx.userId) ||
            todoKeys.includes(ctx.userId) ||
            hvKeys.includes(ctx.userId)
        );
    }
    // team (T-395): nur noch über den Kunden, nicht über die Bearbeiter.
    return kundeVomTeamVerantwortet(v.kunde, ctx);
}

export function todoMatchesScope(t: TodoDto, scope: ScopeKey, ctx: ScopeCtx): boolean {
    if (scope === "alle") return true;
    // „Persönlich" = Bearbeiter ODER Hauptverantwortliche/r des To-dos (D2,
    // Review 2026-07-06) — analog zum Vorgang-Zweig oben. Der Ersteller zählt
    // seit PO 04.09.2026 nicht mehr mit.
    const keys = bearbeiterKeys(t.bearbeiter);
    if (scope === "persoenlich") {
        if (!ctx.userId) return false;
        const hvKeys = t.hauptverantwortlich ?? [];
        return keys.includes(ctx.userId) || hvKeys.includes(ctx.userId);
    }
    // team (T-395): dieselbe Regel wie beim Vorgang, über den Kunden des To-dos.
    return kundeVomTeamVerantwortet(t.kunde, ctx);
}
